"use client";

import { orpc } from "@shared/lib/orpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { Textarea } from "@ui/components/textarea";
import { toast } from "@ui/components/toast";
import { ToggleGroup, ToggleGroupItem } from "@ui/components/toggle-group";
import { cn } from "@ui/lib";
import {
	MessageCircleIcon,
	MessageCircleOffIcon,
	MinusIcon,
	PlusIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { mapsSearchLink, orderName, parsePoint } from "./meta";
import type { OrderDetail, OrderStatus, ShopperGets as Preview } from "./types";

/*
 * The dialogs behind an order's actions. Each one shows what the shopper gets before staff
 * confirm (spec 5.3: "Shopper gets", with the template and its cost when the 24 h window is
 * closed), then ends in a toast and the order as the server returns it.
 */

type PreviewAction =
	| "accept"
	| "reject"
	| "status"
	| "edit_lines"
	| "cancel"
	| "payment_link";

/** Keeps the open order and every queue in step after an action. */
export function useOrderUpdated(organizationId: string) {
	const queryClient = useQueryClient();
	return (order: OrderDetail) => {
		queryClient.setQueryData(
			orpc.orders.get.queryKey({
				input: { organizationId, orderId: order.id },
			}),
			order,
		);
		return queryClient.invalidateQueries(
			{ queryKey: orpc.orders.key() },
			{ cancelRefetch: false },
		);
	};
}

export function actionFailed(error: unknown, title: string) {
	toast.add({
		type: "error",
		title,
		description:
			error instanceof Error && error.message
				? error.message
				: "Nothing changed. Try again in a moment.",
	});
}

/** "The shopper gets": the message, how it goes out and what it costs. */
export function ShopperGets({
	organizationId,
	orderId,
	action,
	status,
}: {
	organizationId: string;
	orderId: string;
	action: PreviewAction;
	status?: OrderStatus;
}) {
	const preview = useQuery(
		orpc.orders.preview.queryOptions({
			input: {
				organizationId,
				orderId,
				action,
				...(status ? { status } : {}),
			},
		}),
	);
	return (
		<section
			aria-label="What the shopper gets"
			className="flex flex-col gap-2 rounded-2xl bg-muted/60 p-3"
		>
			<span className="font-medium text-muted-foreground text-xs">
				Shopper gets
			</span>
			{preview.isPending ? (
				<div className="flex flex-col gap-1.5">
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-2/3" />
				</div>
			) : preview.isError ? (
				<p className="text-muted-foreground text-sm">
					Couldn't load the preview. The shopper is told as your
					ordering setup says.
				</p>
			) : preview.data.sends ? (
				<PreviewMessage preview={preview.data} />
			) : (
				<p className="flex items-center gap-1.5 text-muted-foreground text-sm">
					<MessageCircleOffIcon className="size-3.5" aria-hidden />
					Nothing is sent to the shopper.
				</p>
			)}
		</section>
	);
}

/** The message the shopper gets, and whether it goes as a template or a free message. */
function PreviewMessage({ preview }: { preview: Preview }) {
	return (
		<>
			{preview.text ? (
				<p
					dir="auto"
					className="whitespace-pre-line text-pretty rounded-xl bg-card px-3 py-2 text-left text-sm ring-1 ring-foreground/6"
				>
					{preview.text}
				</p>
			) : null}
			<div className="flex flex-wrap items-center gap-1.5 text-muted-foreground text-xs">
				<MessageCircleIcon className="size-3.5" aria-hidden />
				{preview.mode === "template" ? (
					<>
						<span>Template</span>
						{preview.templateName ? (
							<Badge variant="outline" className="font-mono">
								{preview.templateName}
							</Badge>
						) : null}
						<span>
							· the chat window is closed · about $
							{preview.costUsd.toFixed(3)}
						</span>
					</>
				) : (
					<span>A WhatsApp message, free inside the window</span>
				)}
			</div>
		</>
	);
}

const ETA_CHOICES = [15, 30, 45, 60, 90, 120] as const;

export function AcceptDialog({
	order,
	organizationId,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [eta, setEta] = useState<number>(45);
	const updated = useOrderUpdated(organizationId);
	const accept = useMutation(
		orpc.orders.accept.mutationOptions({
			onSuccess: async (next) => {
				toast.add({
					type: "success",
					title: `Order ${orderName(next)} accepted`,
				});
				onOpenChange(false);
				await updated(next);
			},
			onError: (error) =>
				actionFailed(error, "Couldn't accept the order"),
		}),
	);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Accept order {orderName(order)}</DialogTitle>
					<DialogDescription>
						The shopper is told when to expect it.
					</DialogDescription>
				</DialogHeader>
				<Field>
					<FieldLabel id="eta-label">Ready in</FieldLabel>
					<ToggleGroup
						aria-labelledby="eta-label"
						value={[String(eta)]}
						onValueChange={(value) => {
							const next = Number(value[0]);
							if (next) {
								setEta(next);
							}
						}}
						variant="outline"
						size="sm"
						className="flex-wrap"
					>
						{ETA_CHOICES.map((minutes) => (
							<ToggleGroupItem
								key={minutes}
								value={String(minutes)}
								className="px-3 tabular"
							>
								{minutes < 60
									? `${minutes} min`
									: `${minutes / 60} h`}
							</ToggleGroupItem>
						))}
					</ToggleGroup>
				</Field>
				{open ? (
					<ShopperGets
						organizationId={organizationId}
						orderId={order.id}
						action="accept"
					/>
				) : null}
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Not now
					</Button>
					<Button
						onClick={() =>
							accept.mutate({
								organizationId,
								orderId: order.id,
								etaMinutes: eta,
							})
						}
						disabled={accept.isPending}
					>
						{accept.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Accept order
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/** Moves an order to its next status after showing what the shopper gets. */
export function StatusDialog({
	order,
	organizationId,
	status,
	label,
	done,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	status: OrderStatus;
	label: string;
	done: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const updated = useOrderUpdated(organizationId);
	const setStatus = useMutation(
		orpc.orders.setStatus.mutationOptions({
			onSuccess: async (next) => {
				toast.add({
					type: "success",
					title: `Order ${orderName(next)}: ${done.toLowerCase()}`,
				});
				onOpenChange(false);
				await updated(next);
			},
			onError: (error) =>
				actionFailed(error, "Couldn't update the order"),
		}),
	);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{label}: order {orderName(order)}
					</DialogTitle>
				</DialogHeader>
				{open ? (
					<ShopperGets
						organizationId={organizationId}
						orderId={order.id}
						action="status"
						status={status}
					/>
				) : null}
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Not now
					</Button>
					<Button
						onClick={() =>
							setStatus.mutate({
								organizationId,
								orderId: order.id,
								status,
							})
						}
						disabled={setStatus.isPending}
					>
						{setStatus.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						{label}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/** Reject a new order or cancel an accepted one: a reason, then a destructive confirm. */
export function StopOrderDialog({
	order,
	organizationId,
	kind,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	kind: "reject" | "cancel";
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [reason, setReason] = useState("");
	const updated = useOrderUpdated(organizationId);
	const options = {
		onSuccess: async (next: OrderDetail) => {
			toast.add({
				type: "success",
				title: `Order ${orderName(next)} ${kind === "reject" ? "rejected" : "cancelled"}`,
			});
			setReason("");
			onOpenChange(false);
			await updated(next);
		},
		onError: (error: Error) =>
			actionFailed(
				error,
				kind === "reject"
					? "Couldn't reject the order"
					: "Couldn't cancel the order",
			),
	};
	const reject = useMutation(orpc.orders.reject.mutationOptions(options));
	const cancel = useMutation(orpc.orders.cancel.mutationOptions(options));
	const pending = reject.isPending || cancel.isPending;
	const verb = kind === "reject" ? "Reject" : "Cancel";
	const submit = () => {
		const input = {
			organizationId,
			orderId: order.id,
			reason: reason.trim(),
		};
		if (kind === "reject") {
			reject.mutate(input);
		} else {
			cancel.mutate(input);
		}
	};
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{verb} order {orderName(order)}?
					</DialogTitle>
					<DialogDescription>
						The shopper's order stops here and cannot be reopened.
					</DialogDescription>
				</DialogHeader>
				<Field>
					<FieldLabel htmlFor="stop-reason">Reason</FieldLabel>
					<Textarea
						id="stop-reason"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Out of stock at this branch, closed early…"
						maxLength={500}
						rows={3}
					/>
					<FieldDescription>
						The shopper sees this reason.
					</FieldDescription>
				</Field>
				{open ? (
					<ShopperGets
						organizationId={organizationId}
						orderId={order.id}
						action={kind}
					/>
				) : null}
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Keep the order
					</Button>
					<Button
						variant="destructive"
						onClick={submit}
						disabled={pending || reason.trim().length === 0}
					>
						{pending ? <Spinner data-icon="inline-start" /> : null}
						{verb} order {orderName(order)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/**
 * The shopper typed an area, so nothing is delivered until staff set the point (spec 3.7).
 * A Google Maps link or coordinates; the area they typed opens a Maps search to find it.
 */
export function ConfirmAddressDialog({
	order,
	organizationId,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [text, setText] = useState("");
	const [note, setNote] = useState("");
	const point = parsePoint(text);
	const updated = useOrderUpdated(organizationId);
	const confirm = useMutation(
		orpc.orders.confirmAddress.mutationOptions({
			onSuccess: async (next) => {
				toast.add({
					type: "success",
					title: "Delivery point set",
					description: `Order ${orderName(next)} can be accepted now.`,
				});
				setText("");
				setNote("");
				onOpenChange(false);
				await updated(next);
			},
			onError: (error) =>
				actionFailed(error, "Couldn't set the delivery point"),
		}),
	);
	const address = order.deliveryAddress;
	const typed = [
		address?.area,
		address?.building,
		address?.landmark,
		address?.notes,
	]
		.filter(Boolean)
		.join(", ");
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Confirm the delivery address</DialogTitle>
					<DialogDescription>
						The shopper typed an area but sent no pin.
					</DialogDescription>
				</DialogHeader>
				{typed ? (
					<div className="flex flex-col gap-1 rounded-2xl bg-muted/60 p-3">
						<span className="font-medium text-muted-foreground text-xs">
							Shopper wrote
						</span>
						<p dir="auto" className="text-left text-sm">
							{typed}
						</p>
						<a
							href={mapsSearchLink(typed)}
							target="_blank"
							rel="noreferrer"
							className="w-fit text-sm underline underline-offset-4"
						>
							Find it on Google Maps
						</a>
					</div>
				) : null}
				<Field
					data-invalid={(text.trim() !== "" && !point) || undefined}
				>
					<FieldLabel htmlFor="address-point">
						Google Maps link or coordinates
					</FieldLabel>
					<Input
						id="address-point"
						value={text}
						onChange={(e) => setText(e.target.value)}
						placeholder="https://maps.app.goo.gl/… or 33.8938, 35.5018"
						aria-invalid={
							(text.trim() !== "" && !point) || undefined
						}
					/>
					<FieldDescription>
						{text.trim() === ""
							? "A shortened maps.app.goo.gl link has no coordinates: open it and copy the address bar."
							: point
								? `Point ${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)}`
								: "No coordinates in this. Paste the full Maps link or type them."}
					</FieldDescription>
				</Field>
				<Field>
					<FieldLabel htmlFor="address-note">
						Note for the driver
					</FieldLabel>
					<Input
						id="address-note"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Called the shopper: blue gate next to the pharmacy"
						maxLength={500}
					/>
				</Field>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Not now
					</Button>
					<Button
						onClick={() =>
							point &&
							confirm.mutate({
								organizationId,
								orderId: order.id,
								...point,
								...(note.trim() ? { note: note.trim() } : {}),
							})
						}
						disabled={!point || confirm.isPending}
					>
						{confirm.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Set delivery point
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function NoteDialog({
	order,
	organizationId,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [note, setNote] = useState("");
	const updated = useOrderUpdated(organizationId);
	const add = useMutation(
		orpc.orders.addNote.mutationOptions({
			onSuccess: async (next) => {
				toast.add({ type: "success", title: "Note added" });
				setNote("");
				onOpenChange(false);
				await updated(next);
			},
			onError: (error) => actionFailed(error, "Couldn't add the note"),
		}),
	);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						Add a note to order {orderName(order)}
					</DialogTitle>
					<DialogDescription>
						Only your team sees notes.
					</DialogDescription>
				</DialogHeader>
				<Textarea
					aria-label="Note"
					value={note}
					onChange={(e) => setNote(e.target.value)}
					rows={4}
					maxLength={2000}
					placeholder="Shopper asked to ring twice"
				/>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Not now
					</Button>
					<Button
						onClick={() =>
							add.mutate({
								organizationId,
								orderId: order.id,
								note: note.trim(),
							})
						}
						disabled={add.isPending || note.trim().length === 0}
					>
						{add.isPending ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Add note
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/** Sends one shopper-facing action that has no input of its own (payment link, resend). */
export function SendDialog({
	order,
	organizationId,
	kind,
	open,
	onOpenChange,
}: {
	order: OrderDetail;
	organizationId: string;
	kind: "payment_link" | "resend";
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const updated = useOrderUpdated(organizationId);
	const options = {
		onSuccess: async (next: OrderDetail) => {
			toast.add({
				type: "success",
				title:
					kind === "payment_link"
						? "Payment link sent"
						: "Update sent again",
			});
			onOpenChange(false);
			await updated(next);
		},
		onError: (error: Error) =>
			actionFailed(
				error,
				kind === "payment_link"
					? "Couldn't send the payment link"
					: "Couldn't send the update",
			),
	};
	const payment = useMutation(
		orpc.orders.sendPaymentLink.mutationOptions(options),
	);
	const resend = useMutation(
		orpc.orders.resendUpdate.mutationOptions(options),
	);
	const pending = payment.isPending || resend.isPending;
	const last = [...order.events].reverse().find((e) => e.shopperMessage);
	let body: ReactNode = null;
	if (kind === "payment_link") {
		body = open ? (
			<ShopperGets
				organizationId={organizationId}
				orderId={order.id}
				action="payment_link"
			/>
		) : null;
	} else if (last?.shopperMessage?.text) {
		body = (
			<p
				dir="auto"
				className="whitespace-pre-line rounded-2xl bg-muted/60 p-3 text-left text-sm"
			>
				{last.shopperMessage.text}
			</p>
		);
	}
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{kind === "payment_link"
							? "Send a payment link"
							: "Send the last update again"}
					</DialogTitle>
				</DialogHeader>
				{body}
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Not now
					</Button>
					<Button
						onClick={() => {
							const input = { organizationId, orderId: order.id };
							if (kind === "payment_link") {
								payment.mutate(input);
							} else {
								resend.mutate({
									...input,
									...(last ? { eventId: last.id } : {}),
								});
							}
						}}
						disabled={pending}
					>
						{pending ? <Spinner data-icon="inline-start" /> : null}
						Send to the shopper
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/** A quantity stepper row for the line editor. */
export function QuantityStepper({
	value,
	onChange,
	label,
	disabled,
}: {
	value: number;
	onChange: (value: number) => void;
	label: string;
	disabled?: boolean;
}) {
	return (
		<div
			className={cn(
				"flex items-center rounded-2xl ring-1 ring-foreground/10",
				disabled && "opacity-50",
			)}
		>
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={() => onChange(Math.max(0, value - 1))}
				disabled={disabled || value === 0}
				aria-label={`One fewer ${label}`}
			>
				<MinusIcon />
			</Button>
			<span
				className="w-7 text-center text-sm tabular"
				aria-live="polite"
			>
				{value}
			</span>
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={() => onChange(value + 1)}
				disabled={disabled}
				aria-label={`One more ${label}`}
			>
				<PlusIcon />
			</Button>
		</div>
	);
}
